import { ref, watch } from "vue";
import { deepClone } from "@/helpers/utilities";
import { logWarn } from "@/helpers/logError";

/**
 * Shared composable for filter menu components (UserFilter, TagFilter, CustomerFilter, etc.).
 *
 * Encapsulates the common menu open/close state, selected/applied item tracking,
 * add/remove/isSelected/toggleSelected helpers, and the clear + applyFilter patterns
 * that are duplicated across every filter component. Consumers may bind containerRef
 * to a FilterMenuList exposing resetScroll() so closing the menu resets its list position.
 *
 * @param onMenuOpen  Optional async callback invoked when the menu opens (e.g. to reload data).
 * @param onMenuClose Optional callback invoked when the menu closes (e.g. to reset search query).
 */
export function useFilterMenu<T extends { id: string }>(onMenuOpen?: () => void | Promise<void>, onMenuClose?: () => void) {
	const isFilterOpen = ref<boolean>(false);
	const selectedItems = ref<T[]>([]);
	const appliedItems = ref<T[]>([]);
	const containerRef = ref<any>(null);

	function addSelected(item: T): void {
		selectedItems.value.push(item);
	}

	function removeSelected(item: T): void {
		selectedItems.value = selectedItems.value.filter((c) => c.id !== item.id);
	}

	function isSelected(item: T): boolean {
		return selectedItems.value.some((c) => c.id === item.id);
	}

	function toggleSelected(item: T): void {
		if (isSelected(item)) {
			removeSelected(item);
		} else {
			addSelected(item);
		}
	}

	function closeMenu(): void {
		isFilterOpen.value = false;
		// LSP-16836: the list body is a FilterMenuList that owns its own scroll container; ask it to
		// reset rather than reaching into a Vuetify component's $el. The whole read+call is wrapped in
		// one try/catch — a throwing accessor (break-it round 3) or a throwing resetScroll() (round 2)
		// must not propagate and break the close/apply/clear flow that called it — and resetScroll is
		// read into a local once so a getter can't return a different value between the typeof check
		// and the call (round 3), unlike a bare `?.()` which also throws on a truthy non-function
		// value (round 1).
		try {
			const resetScroll = containerRef.value?.resetScroll;
			if (typeof resetScroll === "function") {
				resetScroll.call(containerRef.value);
			}
		} catch (error) {
			// logWarn's context is forwarded to Datadog as-is; an Error's message/stack aren't own
			// enumerable properties, so embedding the raw Error object serializes to "{}" and loses
			// them — extract the fields Datadog can actually render (mirrors the same fix already
			// applied in services/uploads/adminUploadTelemetry.ts's contextWithError()).
			let errorContext: Record<string, unknown>;
			if (error instanceof Error) {
				errorContext = { errorName: error.name, errorMessage: error.message };
			} else {
				// String(error) itself can throw (a throwing/absent toString) — never let a
				// diagnostic-logging fallback itself become an uncaught throw inside this catch.
				let errorMessage: string;
				try {
					errorMessage = String(error);
				} catch {
					errorMessage = "<unstringifiable error>";
				}
				// errorName (not `error`), matching contextWithError()'s field name so Datadog can
				// query a consistent field across both the Error and non-Error branches.
				errorContext = { errorName: typeof error, errorMessage };
			}
			logWarn("useFilterMenu.closeMenu: resetScroll() threw, menu still closed", errorContext);
		}
	}

	/**
	 * Copy selectedItems → appliedItems, close the menu, and call the provided emit callback.
	 * Pass `emitEvent = false` to silently apply without firing an event (e.g. on mount).
	 */
	function applyFilter(emitCallback: () => void, emitEvent: boolean = true): void {
		appliedItems.value = deepClone([...selectedItems.value]);
		if (emitEvent) {
			emitCallback();
		}
		closeMenu();
	}

	/**
	 * Clear both selected and applied, close the menu, and conditionally emit with empty array.
	 */
	function clear(emitCallback: () => void, emitEvent: boolean = false): void {
		const hadApplied = appliedItems.value.length > 0;
		selectedItems.value = [];
		appliedItems.value = [];
		closeMenu();
		if (emitEvent && hadApplied) {
			emitCallback();
		}
	}

	// When the menu opens: restore selectedItems from appliedItems.
	// When the menu closes: clear selectedItems (caller handles any search query reset via onMenuClose).
	watch(isFilterOpen, async (newVal: boolean, _oldVal, onCleanup) => {
		if (newVal) {
			let stale = false;
			onCleanup(() => {
				stale = true;
			});
			selectedItems.value = deepClone([...appliedItems.value]);
			await onMenuOpen?.();
			if (stale) {
				selectedItems.value = [];
			}
		} else {
			selectedItems.value = [];
			onMenuClose?.();
		}
	});

	return {
		isFilterOpen,
		selectedItems,
		appliedItems,
		containerRef,
		addSelected,
		removeSelected,
		isSelected,
		toggleSelected,
		closeMenu,
		applyFilter,
		clear
	};
}
